import React, { useState } from "react";
import {
  Box,
  Chip,
  Popover,
  TextField,
  IconButton,
} from "@mui/material";
import { DateCalendar, PickersDay, PickersDayProps } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

interface MultiDateSelectProps {
  label?: string;
  value: Dayjs[];
  onChange: (newDates: Dayjs[]) => void;
}

export default function MultiDateSelect({
  label,
  value,
  onChange,
}: MultiDateSelectProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpen = (e: any) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);

  const toggleDate = (dateObj: Dayjs) => {
    const dateStr = dateObj.format("YYYY-MM-DD");
    const exists = value.some((d) => d.format("YYYY-MM-DD") === dateStr);

    const updated = exists
      ? value.filter((d) => d.format("YYYY-MM-DD") !== dateStr)
      : [...value, dateObj];

    onChange(updated);
  };

  return (
    <>
      {/* Input Box with chips */}
      <Box>
        <TextField
          label={label}
          fullWidth
          value=""
          InputProps={{
            readOnly: true,
            endAdornment: (
              <IconButton onClick={handleOpen}>
                <CalendarMonthIcon />
              </IconButton>
            ),
          }}
        />

        {/* Chips representing selected dates */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
          {value.map((d, i) => (
            <Chip
              key={i}
              label={d.format("DD MMM YYYY")}
              onDelete={() =>
                onChange(value.filter((x) => x.format("YYYY-MM-DD") !== d.format("YYYY-MM-DD")))
              }
              color="primary"
              size="small"
              variant="outlined"
            />
          ))}
        </Box>
      </Box>

      {/* Dropped Calendar */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <DateCalendar
          value={null}
          minDate={dayjs().startOf("day")}
          onChange={(d) => d && toggleDate(d)}
          slots={{
            day: (props: PickersDayProps<Dayjs>) => {
              const { day } = props;
              if (day === null) {
                return <PickersDay {...props} />;
              }

              const dateStr = day.format("YYYY-MM-DD");
              const isSelected = value.some((d) => d.format("YYYY-MM-DD") === dateStr);

              return (
                <PickersDay
                  {...props}
                  sx={{
                    bgcolor: isSelected ? "#009ddf !important" : undefined,
                    color: isSelected ? "#fff !important" : undefined,
                  }}
                />
              );
            },
          }}
        />
      </Popover>
    </>
  );
}